import {
  GROUP_TIMELINE_FAMILY_LABELS,
  type GroupTimelineColorMode,
  type GroupTimelineExtraTasksByFamily,
  type GroupTimelineFamily,
  type GroupTimelineLayoutMode,
  type GroupTimelineSortMode,
} from './groupTimeline';
import type { GroupTimelineDetailFilter } from './groupTimelineFilter';
import { EMPTY_GROUP_TIMELINE_FILTER } from './groupTimelineFilter';
import type { StructureFilterValue } from './ganttSubtaskRows';

export const GANTT_VIEW_SYNC_VERSION = 1 as const;

export type GanttTimelineViewMode = 'single' | 'all';

export type GanttTimelineRoomMode = 'single' | 'all';

/** All Projects families share one room so presence is visible on every chip. */
export function ganttTimelineRoomId(params: {
  mode: GanttTimelineRoomMode;
  projectId?: string | null;
  family?: GroupTimelineFamily | null;
}): string {
  if (params.mode === 'single') {
    const id = (params.projectId || '').trim() || '_unknown';
    return `project:${id}`.slice(0, 700);
  }
  return 'all';
}

export function ganttPeerFamilyLabel(outline: GanttSyncedOutline | null | undefined): string | null {
  if (!outline || outline.mode !== 'all') return null;
  return GROUP_TIMELINE_FAMILY_LABELS[outline.family] || null;
}

export type GanttSyncedZoom = 'day' | 'week' | 'month' | 'fit';

/** Shared camera. columnWidth is pixels per day so linked panes zoom by the same amount. */
export type GanttSyncedViewport = {
  zoom: GanttSyncedZoom;
  anchorMs: number;
  scrollTop: number;
  columnWidth?: number;
};

const GANTT_SYNCED_ZOOMS = new Set<GanttSyncedZoom>(['day', 'week', 'month', 'fit']);

export function parseGanttSyncedViewport(raw: unknown): GanttSyncedViewport | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  if (typeof row.zoom !== 'string' || !GANTT_SYNCED_ZOOMS.has(row.zoom as GanttSyncedZoom)) return null;
  if (typeof row.anchorMs !== 'number' || !Number.isFinite(row.anchorMs)) return null;
  const scrollTop = typeof row.scrollTop === 'number' && Number.isFinite(row.scrollTop) ? row.scrollTop : 0;
  const columnWidth = typeof row.columnWidth === 'number' && Number.isFinite(row.columnWidth) && row.columnWidth > 0
    ? row.columnWidth
    : undefined;
  return { zoom: row.zoom as GanttSyncedZoom, anchorMs: row.anchorMs, scrollTop, columnWidth };
}

export function ganttAnchorMsFromScroll(rangeStartMs: number, scrollLeft: number, cellWidth: number): number {
  if (!(cellWidth > 0)) return rangeStartMs;
  return rangeStartMs + (scrollLeft / cellWidth) * 86400000;
}

export function ganttScrollLeftForAnchor(rangeStartMs: number, anchorMs: number, cellWidth: number): number {
  if (!(cellWidth > 0)) return 0;
  return Math.max(0, ((anchorMs - rangeStartMs) / 86400000) * cellWidth);
}

export function defaultSplitTimelineFamily(current: GroupTimelineFamily): GroupTimelineFamily {
  return current === 'production' ? 'procurement' : 'production';
}

export type GanttGanttOutlineSlice = {
  collapsedGroups: string[];
  collapsedSections: string[];
  expandedParentIds: string[];
  pinKeys: string[];
};

export type GanttSyncedOutline =
  | {
      version: typeof GANTT_VIEW_SYNC_VERSION;
      mode: 'single';
      viewHash: string;
      structureFilter: StructureFilterValue[] | null;
      gantt: GanttGanttOutlineSlice;
    }
  | {
      version: typeof GANTT_VIEW_SYNC_VERSION;
      mode: 'all';
      viewHash: string;
      family: GroupTimelineFamily;
      colorMode: GroupTimelineColorMode;
      layout: GroupTimelineLayoutMode;
      sort: GroupTimelineSortMode;
      filter: GroupTimelineDetailFilter;
      extraTasks: GroupTimelineExtraTasksByFamily;
      gantt: GanttGanttOutlineSlice;
    };

function stableStringify(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) {
    const items = [...value];
    if (items.length > 1 && items.every((x) => typeof x === 'string' || typeof x === 'number')) {
      items.sort((a, b) => String(a).localeCompare(String(b)));
    }
    return `[${items.map(stableStringify).join(',')}]`;
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`;
}

/** FNV-1a 32-bit hash as hex — stable view fingerprint for cursor matching. */
export function hashGanttViewPayload(payload: unknown): string {
  const str = stableStringify(payload);
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

function sortedSet(values: Iterable<string>): string[] {
  return Array.from(new Set(values)).sort();
}

function normalizeStructureFilter(
  filter: Set<StructureFilterValue> | null | undefined,
): StructureFilterValue[] | null {
  if (!filter || filter.size === 0) return null;
  return sortedSet(Array.from(filter).map(String)) as StructureFilterValue[];
}

function normalizeExtraTasks(map: GroupTimelineExtraTasksByFamily): GroupTimelineExtraTasksByFamily {
  const out: GroupTimelineExtraTasksByFamily = {};
  const keys = Object.keys(map).sort() as GroupTimelineFamily[];
  for (const k of keys) {
    const ids = map[k];
    if (Array.isArray(ids) && ids.length) {
      out[k] = sortedSet(ids);
    }
  }
  return out;
}

function normalizeFilter(filter: GroupTimelineDetailFilter): GroupTimelineDetailFilter {
  return {
    projectIds: sortedSet(filter.projectIds),
    sectionIds: sortedSet(filter.sectionIds),
    phases: sortedSet(filter.phases) as GroupTimelineDetailFilter['phases'],
    statuses: sortedSet(filter.statuses) as GroupTimelineDetailFilter['statuses'],
    departments: sortedSet(filter.departments) as GroupTimelineDetailFilter['departments'],
    supplierOrgIds: sortedSet(filter.supplierOrgIds),
    simulated: filter.simulated,
    taskIds: sortedSet(filter.taskIds),
  };
}

export function buildGanttGanttOutlineSlice(params: {
  collapsedGroups: Set<string>;
  collapsedSections: Set<string>;
  expandedParentIds: Set<string>;
  pinKeys: string[];
}): GanttGanttOutlineSlice {
  return {
    collapsedGroups: sortedSet(params.collapsedGroups),
    collapsedSections: sortedSet(params.collapsedSections),
    expandedParentIds: sortedSet(params.expandedParentIds),
    pinKeys: sortedSet(params.pinKeys),
  };
}

export function buildGanttSyncedOutlineSingle(params: {
  structureFilter: Set<StructureFilterValue> | null;
  gantt: GanttGanttOutlineSlice;
}): Extract<GanttSyncedOutline, { mode: 'single' }> {
  const structureFilter = normalizeStructureFilter(params.structureFilter);
  const gantt = {
    collapsedGroups: sortedSet(params.gantt.collapsedGroups),
    collapsedSections: sortedSet(params.gantt.collapsedSections),
    expandedParentIds: sortedSet(params.gantt.expandedParentIds),
    pinKeys: sortedSet(params.gantt.pinKeys),
  };
  const hashPayload = {
    version: GANTT_VIEW_SYNC_VERSION,
    mode: 'single' as const,
    structureFilter,
    gantt,
  };
  return {
    ...hashPayload,
    viewHash: hashGanttViewPayload(hashPayload),
  };
}

export function buildGanttSyncedOutlineAll(params: {
  family: GroupTimelineFamily;
  colorMode: GroupTimelineColorMode;
  layout: GroupTimelineLayoutMode;
  sort: GroupTimelineSortMode;
  filter: GroupTimelineDetailFilter;
  extraTasks: GroupTimelineExtraTasksByFamily;
  gantt: GanttGanttOutlineSlice;
}): Extract<GanttSyncedOutline, { mode: 'all' }> {
  const filter = normalizeFilter(params.filter);
  const extraTasks = normalizeExtraTasks(params.extraTasks);
  const gantt = {
    collapsedGroups: sortedSet(params.gantt.collapsedGroups),
    collapsedSections: sortedSet(params.gantt.collapsedSections),
    expandedParentIds: sortedSet(params.gantt.expandedParentIds),
    pinKeys: sortedSet(params.gantt.pinKeys),
  };
  const hashPayload = {
    version: GANTT_VIEW_SYNC_VERSION,
    mode: 'all' as const,
    family: params.family,
    colorMode: params.colorMode,
    layout: params.layout,
    sort: params.sort,
    filter,
    extraTasks,
    gantt,
  };
  return {
    ...hashPayload,
    viewHash: hashGanttViewPayload(hashPayload),
  };
}

export type ApplyGanttSyncedOutlineHandlers = {
  setStructureFilter?: (next: Set<StructureFilterValue> | null) => void;
  setGroupTimelineFamily?: (family: GroupTimelineFamily) => void;
  setGroupTimelineColorMode?: (mode: GroupTimelineColorMode) => void;
  setGroupTimelineLayout?: (mode: GroupTimelineLayoutMode) => void;
  setGroupTimelineSort?: (mode: GroupTimelineSortMode) => void;
  setGroupTimelineFilter?: (filter: GroupTimelineDetailFilter) => void;
  setGroupTimelineExtraTasks?: (map: GroupTimelineExtraTasksByFamily) => void;
  applyGanttOutline: (gantt: GanttGanttOutlineSlice) => void;
};

export function applyGanttSyncedOutline(
  outline: GanttSyncedOutline,
  handlers: ApplyGanttSyncedOutlineHandlers,
): void {
  if (outline.mode === 'single') {
    if (handlers.setStructureFilter) {
      const sf = outline.structureFilter;
      handlers.setStructureFilter(
        sf && sf.length ? new Set(sf as StructureFilterValue[]) : null,
      );
    }
  } else {
    handlers.setGroupTimelineFamily?.(outline.family);
    handlers.setGroupTimelineColorMode?.(outline.colorMode);
    handlers.setGroupTimelineLayout?.(outline.layout);
    handlers.setGroupTimelineSort?.(outline.sort);
    handlers.setGroupTimelineFilter?.({ ...EMPTY_GROUP_TIMELINE_FILTER, ...outline.filter });
    handlers.setGroupTimelineExtraTasks?.({ ...outline.extraTasks });
  }
  handlers.applyGanttOutline(outline.gantt);
}

export function ganttOutlineSliceToSets(slice: GanttGanttOutlineSlice): {
  collapsedGroups: Set<string>;
  collapsedSections: Set<string>;
  expandedParentIds: Set<string>;
  pinKeys: string[];
} {
  return {
    collapsedGroups: new Set(slice.collapsedGroups),
    collapsedSections: new Set(slice.collapsedSections),
    expandedParentIds: new Set(slice.expandedParentIds),
    pinKeys: [...slice.pinKeys],
  };
}

/** Snap local calendar date to UTC midnight ms (matches Gantt dayIndex rounding). */
export function snapGanttTimelineDayMs(d: Date): number {
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  return target.getTime();
}
